/**
 * PURPOSE: "Plazo de envío de elegibles" por fecha del fixture (FTR-0015, corrección del propietario del 2026-09-11).
 * RESPONSIBILITIES: Selector de solo fechas numeradas del fixture (nunca `Día DD/MM/YYYY`); por la fecha elegida,
 *   exactamente uno de dos modos vigentes — "Cerrar antes de cada partido" (plazo relativo EVENT_SPORT, la base
 *   segura) o "Usar una fecha y hora común para esta fecha" (overrides MATCH absolutos en sus partidos abiertos);
 *   fecha y hora obligatorias con la zona de Lima explícita, vista previa de cuántos partidos se verán afectados
 *   antes de confirmar; volver al modo relativo retira solo los overrides de esa fecha y restaura de inmediato el
 *   cálculo relativo, sin desactivar la política base ni tocar otras fechas. Nunca pide un motivo escrito: envía
 *   siempre una descripción fija para la auditoría (el backend la exige solo si algún plazo ya venció; nunca se
 *   ofrece un tercer estado "sin plazo").
 * COLLABORATORS: rugbyLineupsService.js, rugbyLineups.utils.js, CompanyRugbyLineupsTab.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/RugbyFixtureDeadlinePanel.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { bulkRugbyMatchDeadlines, clearRugbyFixtureDeadlines } from "../../services/rugbyLineupsService.js";
import { StatusNote } from "./RugbyLineupConfigPanels.jsx";
import {
  FIXTURE_DEADLINE_MODES,
  FIXTURE_DEADLINE_MODE_LABELS,
  FIXTURE_DEADLINE_REASONS,
  buildFixtureRounds,
  describeFixtureRound,
  getRugbyErrorMessage,
} from "./rugbyLineups.utils.js";
import { isPastLimaDateTime } from "./uniformOptions.utils.js";

const selectClass = "w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal";
const ModeButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`rounded-lg border px-3 py-2 text-sm font-semibold ${active ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
  >
    {children}
  </button>
);

const RugbyFixtureDeadlinePanel = ({ scope, matches = [], onSaved }) => {
  const rounds = useMemo(() => buildFixtureRounds(matches), [matches]);
  const [selectedRound, setSelectedRound] = useState("");
  const [mode, setMode] = useState(FIXTURE_DEADLINE_MODES.RELATIVE);
  const [closesAtLima, setClosesAtLima] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedRound((current) => (current && rounds.some((round) => String(round.nroFecha) === current) ? current : rounds[0] ? String(rounds[0].nroFecha) : ""));
  }, [rounds]);

  const round = rounds.find((item) => String(item.nroFecha) === selectedRound) || null;
  const roundNroFecha = round?.nroFecha ?? null;
  const roundMode = round?.mode ?? null;
  const roundClosesAtLima = round?.closesAtLima ?? null;
  useEffect(() => {
    if (roundNroFecha === null) return;
    setMode(roundMode);
    setClosesAtLima(roundClosesAtLima || "");
  }, [roundNroFecha, roundMode, roundClosesAtLima]);

  const status = describeFixtureRound(round);

  const applyFixed = async (event) => {
    event.preventDefault();
    if (!round || !closesAtLima) return toast.error("Elige la fecha del fixture y la fecha y hora límite.");
    if (isPastLimaDateTime(closesAtLima) && !window.confirm("Esa fecha y hora ya pasaron (hora de Lima): los envíos de esa fecha quedarán cerrados. ¿Continuar?")) return undefined;
    if (!window.confirm(`Se fijará esta fecha y hora límite en ${round.openCount} partido(s) abierto(s) de la Fecha ${round.nroFecha}. ¿Continuar?`)) return undefined;
    setSaving(true);
    try {
      const result = await bulkRugbyMatchDeadlines(scope, { nroFecha: round.nroFecha, closesAtLima, reason: FIXTURE_DEADLINE_REASONS.FIXED });
      toast.success(`Fecha y hora límite aplicada a ${result.count} partido(s).`);
      onSaved();
    } catch (error) {
      toast.error(getRugbyErrorMessage(error, "No se pudo aplicar la fecha y hora límite."));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const useRelative = async () => {
    if (!round) return;
    if (!window.confirm(`Los partidos abiertos de la Fecha ${round.nroFecha} volverán a cerrar según el plazo antes de cada partido. ¿Continuar?`)) return;
    setSaving(true);
    try {
      const result = await clearRugbyFixtureDeadlines(scope, { nroFecha: round.nroFecha, reason: FIXTURE_DEADLINE_REASONS.RELATIVE });
      toast.success(`${result.count} partido(s) volvieron al plazo antes de cada partido.`);
      setMode(FIXTURE_DEADLINE_MODES.RELATIVE);
      onSaved();
    } catch (error) {
      toast.error(getRugbyErrorMessage(error, "No se pudo restablecer el plazo."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-bold text-slate-800">Plazo de envío de elegibles</h3>
      <p className="mb-3 text-xs text-slate-500">
        El plazo antes de cada partido (minutos configurados arriba) es la base segura y aplica a todo lo que no tenga una fecha y hora fija propia. Elige
        una fecha del fixture para fijar una fecha y hora común a todos sus partidos abiertos, o volver al plazo antes de cada partido.
      </p>
      {!rounds.length ? (
        <p className="text-sm text-slate-500">Este evento-deporte no tiene partidos con una fecha numerada del fixture.</p>
      ) : (
        <>
          <label className="block text-xs font-semibold text-slate-600">
            Fecha del fixture
            <select value={selectedRound} onChange={(event) => setSelectedRound(event.target.value)} className={`mt-1 ${selectClass}`}>
              {rounds.map((item) => (
                <option key={item.nroFecha} value={item.nroFecha}>
                  {`Fecha ${item.nroFecha}`}
                </option>
              ))}
            </select>
          </label>

          {round && (
            <>
              <div className="mt-3">
                <StatusNote tone={status.tone}>{status.text}</StatusNote>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <ModeButton active={mode === FIXTURE_DEADLINE_MODES.RELATIVE} onClick={() => setMode(FIXTURE_DEADLINE_MODES.RELATIVE)}>
                  {FIXTURE_DEADLINE_MODE_LABELS.RELATIVE}
                </ModeButton>
                <ModeButton active={mode === FIXTURE_DEADLINE_MODES.FIXED} onClick={() => setMode(FIXTURE_DEADLINE_MODES.FIXED)}>
                  {FIXTURE_DEADLINE_MODE_LABELS.FIXED}
                </ModeButton>
              </div>

              {mode === FIXTURE_DEADLINE_MODES.RELATIVE ? (
                <div className="mt-3">
                  {round.mode === FIXTURE_DEADLINE_MODES.FIXED ? (
                    <button type="button" onClick={useRelative} disabled={saving} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                      {saving ? "Aplicando..." : "Usar plazo antes de cada partido"}
                    </button>
                  ) : (
                    <p className="text-xs text-slate-500">Ya rige el plazo antes de cada partido para esta fecha.</p>
                  )}
                </div>
              ) : (
                <form onSubmit={applyFixed} className="mt-3 grid gap-3 sm:grid-cols-3">
                  <label className="text-xs font-semibold text-slate-600 sm:col-span-2">
                    Fecha y hora límite (Lima)
                    <input
                      type="datetime-local"
                      step={60}
                      value={closesAtLima}
                      onChange={(event) => setClosesAtLima(event.target.value)}
                      className={`mt-1 ${selectClass}`}
                    />
                  </label>
                  <div className="flex items-end">
                    <button type="submit" disabled={saving} className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-300">
                      {saving ? "Aplicando..." : "Aplicar fecha y hora límite"}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 sm:col-span-3">
                    Se aplicará a {round.openCount} partido(s) abierto(s) de la Fecha {round.nroFecha}.
                  </p>
                </form>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default RugbyFixtureDeadlinePanel;
